import React, { useState } from 'react';

// Shared renderer for a CLIP description + its SAE explanation.
// `data` shape (same for precomputed gallery JSON and the live /describe service):
//   { sentence, concepts:[{term,category,score}],
//     features:[{atom,act,means:[term,...]}],
//     attribution:[{term, supporting:[{term,contribution}]}] }

const CAT_COLOR = {
  animal: '#2ecc71', flora: '#8bc34a', behavior: '#ffd54f', habitat: '#26c6da',
  light: '#ffcc66', time_weather: '#4ea1ff', color_tone: '#ff9f43',
  composition: '#ff7597', mood: '#b07cff', style: '#9e9e9e',
  // legacy keys (older precomputed data) still get sensible colours
  subject: '#2ecc71', scene: '#26c6da',
};

const uniq = (arr) => arr.filter((v, i) => arr.indexOf(v) === i);

export default function DescribePanel({ data, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!data) return null;
  const { concepts = [], features = [], attribution = [] } = data;
  const maxAct = features.length ? Math.max(...features.map((f) => f.act)) || 1 : 1;

  return (
    <div className="desc-panel">
      <div className="desc-label">What the model sees</div>

      <div className="desc-chips">
        {concepts.map((c, i) => (
          <span
            key={i}
            className="desc-chip"
            style={{ borderColor: CAT_COLOR[c.category] || '#888' }}
            title={`${c.category} · ${(c.score * 100).toFixed(0)}%`}
          >
            {c.term}
          </span>
        ))}
      </div>

      {features.length > 0 && (
        <>
          <button className="desc-why-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? '▾' : '▸'} How it decided — sparse features (SAE)
          </button>
          {open && (
            <div className="desc-why">
              <div className="desc-why-sub">
                Active features, named by the concepts their direction points to:
              </div>
              {features.map((f, i) => (
                <div key={i} className="desc-feat">
                  <span className="desc-feat-name">{uniq(f.means).slice(0, 3).join(' / ')}</span>
                  <span className="desc-feat-bar">
                    <span className="desc-feat-fill" style={{ width: `${Math.max(4, (f.act / maxAct) * 100)}%` }} />
                  </span>
                  <span className="desc-feat-atom">#{f.atom}</span>
                </div>
              ))}
              {attribution.length > 0 && (
                <>
                  <div className="desc-why-sub">Why each tag was chosen:</div>
                  {attribution.slice(0, 6).map((a, i) => (
                    <div key={i} className="desc-attr">
                      <span className="desc-attr-term">{a.term}</span>
                      <span className="desc-attr-arrow">←</span>
                      <span className="desc-attr-src">
                        {uniq(a.supporting.map((s) => s.term)).slice(0, 3).join(', ')}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
